import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { TechMarquee } from '../components/TechMarquee';
import { CompetencyMatrix } from '../components/CompetencyMatrix';
import { ProjectsSection } from '../components/ProjectsSection';
import { EngineeringPrinciples } from '../components/EngineeringPrinciples';
import { ContactSection } from '../components/ContactSection';

import { STACK_ITEMS_BASE, PROJECT_ITEMS_BASE } from '../data/portfolioData';
import { type StackItem } from '../types';

export function PortfolioHome() {
  const { t } = useTranslation();
  const [selectedStackId, setSelectedStackId] = useState<string>(STACK_ITEMS_BASE[0].id);

  // Map base stack items to localized StackItem objects
  const localizedStacks: StackItem[] = STACK_ITEMS_BASE.map((base) => ({
    id: base.id,
    name: base.name,
    categoryKey: base.categoryKey,
    color: base.color,
    category: t(`stacks.${base.id}.category`, { defaultValue: base.categoryKey }),
    experience: t(`stacks.${base.id}.experience`, { defaultValue: 'Professional Experience' }),
    highlight: t(`stacks.${base.id}.highlight`, { defaultValue: '' })
  }));

  return (
    <div className="min-h-screen bg-[#07090E] text-[#E2E8F0] font-sans selection:bg-[#00F0FF] selection:text-[#07090E] relative overflow-x-hidden">
      {/* Background Blueprint Grid Lines */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, #00F0FF 1px, transparent 1px),
            linear-gradient(to bottom, #00F0FF 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Radial Cyan Glows */}
      <div className="fixed top-0 right-0 w-[500px] h-[500px] bg-[#00F0FF]/[0.03] rounded-full blur-[140px] pointer-events-none z-0" />
      <div className="fixed bottom-0 left-0 w-[500px] h-[500px] bg-[#00F0FF]/[0.02] rounded-full blur-[160px] pointer-events-none z-0" />

      {/* Sticky Header with Language Selector & Navigation */}
      <Header />

      {/* Main Content Container */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24 space-y-4">
        <Hero />

        <TechMarquee
          stacks={STACK_ITEMS_BASE}
          selectedStackId={selectedStackId}
          onSelectStackId={setSelectedStackId}
        />

        <CompetencyMatrix
          stacks={localizedStacks}
          selectedStackId={selectedStackId}
          onSelectStackId={setSelectedStackId}
        />

        <ProjectsSection projects={PROJECT_ITEMS_BASE} />

        <EngineeringPrinciples />

        <ContactSection />
      </main>
    </div>
  );
}

export default PortfolioHome;
